import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const PerformanceFilters = ({ onFiltersChange }) => {
  const [filters, setFilters] = useState({
    timeframe: 'ytd',
    assetType: 'all',
    strategy: 'all',
    performance: 'all',
    riskLevel: 'all'
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const timeframeOptions = [
    { value: '1d', label: 'Today' },
    { value: '1w', label: 'This Week' },
    { value: '1m', label: 'This Month' },
    { value: '3m', label: '3 Months' },
    { value: '6m', label: '6 Months' },
    { value: 'ytd', label: 'Year to Date' },
    { value: '1y', label: '1 Year' },
    { value: 'all', label: 'All Time' }
  ];

  const assetTypeOptions = [
    { value: 'all', label: 'All Assets' },
    { value: 'stocks', label: 'Indian Stocks' },
    { value: 'crypto', label: 'Cryptocurrency' },
    { value: 'options', label: 'Options' },
    { value: 'futures', label: 'Futures' },
    { value: 'cash', label: 'Cash' }
  ];

  const strategyOptions = [
    { value: 'all', label: 'All Strategies' },
    { value: 'momentum', label: 'Momentum' },
    { value: 'mean_reversion', label: 'Mean Reversion' },
    { value: 'breakout', label: 'Breakout' },
    { value: 'pairs', label: 'Pairs Trading' },
    { value: 'volatility', label: 'Volatility' },
    { value: 'sector', label: 'Sector Rotation' }
  ];

  const performanceOptions = [
    { value: 'all', label: 'All Performance' },
    { value: 'positive', label: 'Positive Returns' },
    { value: 'negative', label: 'Negative Returns' },
    { value: 'outperforming', label: 'Outperforming' },
    { value: 'underperforming', label: 'Underperforming' }
  ];

  const riskLevelOptions = [
    { value: 'all', label: 'All Risk Levels' },
    { value: 'low', label: 'Low Risk' },
    { value: 'medium', label: 'Medium Risk' },
    { value: 'high', label: 'High Risk' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    if (onFiltersChange) {
      onFiltersChange(newFilters);
    }
  };

  const resetFilters = () => {
    const defaultFilters = {
      timeframe: 'ytd',
      assetType: 'all',
      strategy: 'all',
      performance: 'all',
      riskLevel: 'all'
    };
    setFilters(defaultFilters);
    if (onFiltersChange) {
      onFiltersChange(defaultFilters);
    }
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters)?.filter(value => value !== 'all' && value !== 'ytd')?.length;
  };

  const quickFilters = [
    { key: 'timeframe', value: '1d', label: 'Today', icon: 'Calendar' },
    { key: 'timeframe', value: '1w', label: 'This Week', icon: 'CalendarDays' },
    { key: 'timeframe', value: '1m', label: 'This Month', icon: 'CalendarRange' },
    { key: 'performance', value: 'positive', label: 'Winners', icon: 'TrendingUp' },
    { key: 'performance', value: 'negative', label: 'Losers', icon: 'TrendingDown' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-4">
      {/* Quick Filters */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Icon name="Filter" size={16} className="text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">Filters</span>
          {getActiveFiltersCount() > 0 && (
            <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
              <span className="text-xs font-medium text-primary-foreground">
                {getActiveFiltersCount()}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={resetFilters}
            iconName="RotateCcw"
            iconSize={14}
            className="text-muted-foreground hover:text-foreground"
          >
            Reset
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            iconSize={14}
            className="text-muted-foreground hover:text-foreground"
          >
            {isExpanded ? 'Less' : 'More'}
          </Button>
        </div>
      </div>
      {/* Quick Filter Buttons */}
      <div className="flex flex-wrap gap-2">
        {quickFilters?.map((filter, index) => (
          <button
            key={index}
            onClick={() => handleFilterChange(filter?.key, filter?.value)}
            className={`flex items-center space-x-1 px-3 py-1 rounded-md text-sm font-medium transition-smooth ${
              filters?.[filter?.key] === filter?.value
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
            }`}
          >
            <Icon name={filter?.icon} size={14} />
            <span>{filter?.label}</span>
          </button>
        ))}
      </div>
      {/* Advanced Filters */}
      {isExpanded && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 pt-4 border-t border-border">
          <Select
            label="Timeframe"
            options={timeframeOptions}
            value={filters?.timeframe}
            onChange={(value) => handleFilterChange('timeframe', value)}
            className="w-full"
          />

          <Select
            label="Asset Type"
            options={assetTypeOptions}
            value={filters?.assetType}
            onChange={(value) => handleFilterChange('assetType', value)}
            className="w-full"
          />

          <Select
            label="Strategy"
            options={strategyOptions}
            value={filters?.strategy}
            onChange={(value) => handleFilterChange('strategy', value)}
            className="w-full"
          />

          <Select
            label="Performance"
            options={performanceOptions}
            value={filters?.performance}
            onChange={(value) => handleFilterChange('performance', value)}
            className="w-full"
          />

          <Select
            label="Risk Level"
            options={riskLevelOptions}
            value={filters?.riskLevel}
            onChange={(value) => handleFilterChange('riskLevel', value)}
            className="w-full"
          />
        </div>
      )}
      {/* Active Filters Summary */}
      {getActiveFiltersCount() > 0 && (
        <div className="flex items-center space-x-2 pt-2 border-t border-border">
          <span className="text-xs text-muted-foreground">Active filters:</span>
          <div className="flex flex-wrap gap-1">
            {Object.entries(filters)?.map(([key, value]) => {
              if (value === 'all' || (key === 'timeframe' && value === 'ytd')) return null;
              
              const getFilterLabel = () => {
                switch (key) {
                  case 'timeframe':
                    return timeframeOptions?.find(opt => opt?.value === value)?.label;
                  case 'assetType':
                    return assetTypeOptions?.find(opt => opt?.value === value)?.label;
                  case 'strategy':
                    return strategyOptions?.find(opt => opt?.value === value)?.label;
                  case 'performance':
                    return performanceOptions?.find(opt => opt?.value === value)?.label;
                  case 'riskLevel':
                    return riskLevelOptions?.find(opt => opt?.value === value)?.label;
                  default:
                    return value;
                }
              };

              return (
                <div
                  key={key}
                  className="flex items-center space-x-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-xs"
                >
                  <span>{getFilterLabel()}</span>
                  <button
                    onClick={() => handleFilterChange(key, key === 'timeframe' ? 'ytd' : 'all')}
                    className="hover:bg-primary/20 rounded-full p-0.5"
                  >
                    <Icon name="X" size={10} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceFilters;